import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import PointsRulesSection from "@/components/PointsRulesSection";
import PortalVisibilitySection from "@/components/PortalVisibilitySection";
import AppearanceSection from "@/components/AppearanceSection";
import { Save, Download, AlertTriangle, Sparkles, Palette, MessageSquare, MessageCircle, Building2, Bell, Shield, Trophy, ShieldAlert, PartyPopper, Plug, Clock3, KeyRound, Receipt, ToggleLeft, Users } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import FestivalThemesSection from "@/components/settings/FestivalThemesSection";
import EmailContinuitySection from "@/components/settings/EmailContinuitySection";
import EmailSignatureSettings from "@/components/settings/EmailSignatureSettings";
import ChangePasswordCard from "@/components/settings/ChangePasswordCard";
import LogoUploader from "@/components/settings/LogoUploader";
import PlatformSettingsSection from "@/components/settings/PlatformSettingsSection";
import SessionTimeoutSection from "@/components/settings/SessionTimeoutSection";
import DeleteWorkspaceBlock from "@/components/settings/DeleteWorkspaceBlock";
import CloseAccountBlock from "@/components/settings/CloseAccountBlock";
import AuditBackfillBlock from "@/components/settings/AuditBackfillBlock";
import ImpersonationSessionsBlock from "@/components/settings/ImpersonationSessionsBlock";
import IntegrationsSection from "@/components/settings/IntegrationsSection";
import TimesheetSettingsSection from "@/components/settings/TimesheetSettingsSection";
import CommunicationsSettingsSection from "@/components/settings/CommunicationsSettingsSection";
import PIPSettingsSection from "@/components/settings/PIPSettingsSection";
import { NotificationPrefsPane } from "@/pages/app/MsmeEssentials";

const SETTINGS_TABS = [
  { key: "general", label: "General", icon: Building2 },
  { key: "review-ai", label: "Review & AI", icon: Sparkles },
  { key: "branding", label: "Branding", icon: Palette },
  { key: "themes", label: "Themes", icon: PartyPopper },
  { key: "engagement", label: "Engagement", icon: Trophy },
  { key: "communications", label: "Communications", icon: MessageCircle },
  { key: "timesheet", label: "Timesheet", icon: Clock3 },
  { key: "pip", label: "PIP", icon: ShieldAlert },
  { key: "integrations", label: "Integrations", icon: Plug },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "security", label: "Security", icon: Shield },
  { key: "advanced", label: "Data & Danger", icon: ShieldAlert },
];

export default function Settings() {
  const { activeWorkspaceId, activeWorkspace, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [brand, setBrand] = useState({});
  const [brandBusy, setBrandBusy] = useState(false);

  const planSlug = activeWorkspace?.plan_slug;
  const whiteLabelAllowed = ["agency", "enterprise"].includes(planSlug);

  const load = async () => {
    if (!activeWorkspaceId) return;
    const { data } = await api.get(`/workspaces/${activeWorkspaceId}/settings`);
    setData(data);
    setForm({
      name: data.workspace.name,
      currency: data.workspace.currency,
      timezone: data.workspace.timezone || "Asia/Kolkata",
      slug: data.workspace.slug || "",
      logo_url: data.workspace.logo_url || "",
      brand_color: data.workspace.brand_color || "",
      address: data.workspace.address || "",
      gstin: data.workspace.gstin || "",
      default_comment_visibility: data.settings.default_comment_visibility || "client",
      default_review_link_mode: data.settings.default_review_link_mode || "approve",
      default_link_expiry_days: data.settings.default_link_expiry_days || 30,
      auto_lock_on_approve: data.settings.auto_lock_on_approve ?? true,
      email_notifications_enabled: data.settings.email_notifications_enabled ?? true,
      approval_mode: data.settings.approval_mode || "single",
      ai_access_level: data.settings.ai_access_level || "owner_admin",
      share_message_template: data.settings.share_message_template || "",
    });
    try {
      const { data: b } = await api.get(`/workspaces/${activeWorkspaceId}/branding`);
      setBrand({
        logo_url: b.logo_url || "",
        primary_hsl: b.primary_hsl || "",
        accent_text: b.accent_text || "",
        custom_email_from: b.custom_email_from || "",
      });
    } catch (e) {
      // 404 ok if not set yet
    }
  };

  useEffect(() => { load(); }, [activeWorkspaceId]);

  const save = async () => {
    setBusy(true);
    try {
      // Client-side slug validation — mirrors backend pattern. Surfaces the
      // exact reason on the input BEFORE hitting the API, and lets us skip
      // the network call for obviously-bad slugs.
      if (form.slug) {
        if (!/^[a-z0-9][a-z0-9_-]{1,38}[a-z0-9]$/.test(form.slug)) {
          toast.error("Slug must be 3–40 chars: lowercase letters, digits, hyphens or underscores. Cannot start/end with -_.");
          setBusy(false);
          return;
        }
      }
      const payload = { ...form };
      if (!payload.slug) delete payload.slug;
      // Iter 153 P0 fix — strip empty-string values from optional fields so
      // pydantic pattern/int validators don't 400 on a fresh workspace. The
      // culprits are brand_color, logo_url, address, gstin, share_message_template
      // and default_link_expiry_days when the user hasn't touched them.
      const OPTIONAL_KEYS = [
        "brand_color", "logo_url", "address", "gstin",
        "share_message_template", "default_link_expiry_days",
      ];
      OPTIONAL_KEYS.forEach((k) => {
        if (payload[k] === "" || payload[k] === null || payload[k] === undefined) {
          delete payload[k];
        }
      });
      // Normalise brand_color — accept #hex or hex; if user typed without #, prepend it.
      if (payload.brand_color && !payload.brand_color.startsWith("#")) {
        payload.brand_color = `#${payload.brand_color}`;
      }
      await api.patch(`/workspaces/${activeWorkspaceId}/settings`, payload);
      toast.success("Settings saved");
      await refresh();
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to save settings"));
    } finally { setBusy(false); }
  };

  const saveBrand = async () => {
    setBrandBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(brand).filter(([, v]) => v !== ""));
      await api.post(`/workspaces/${activeWorkspaceId}/branding`, payload);
      toast.success("White-label branding saved");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to save branding"));
    } finally { setBrandBusy(false); }
  };

  const exportData = async () => {
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/data-export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `creativeops-export-${activeWorkspaceId.slice(0, 8)}-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  if (!data) return <div className="p-10 text-muted-foreground">Loading…</div>;

  return (
    <>
      <PageHeader
        eyebrow="Workspace"
        title="Settings"
        description="Profile, defaults, AI, branding, notifications, themes and data — grouped."
        testid="settings-header"
        actions={<Button onClick={save} disabled={busy} className="rounded-none" data-testid="settings-save-btn"><Save className="w-4 h-4 mr-2" />{busy ? "Saving…" : "Save changes"}</Button>}
      />
      <SettingsTabsShell>
        {(tab) => (
          <>
            {tab === "general" && (
              <>
                {/* Profile */}
                <div className="border border-border p-6 space-y-5">
                  <div className="label-eyebrow">Profile</div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Workspace name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="set-name-input" />
                    <div>
                      <Label className="label-eyebrow mb-2 inline-block">Currency</Label>
                      <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                        <SelectTrigger className="h-10" data-testid="set-currency-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="INR">INR — Indian Rupees</SelectItem>
                          <SelectItem value="USD">USD — US Dollars</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Field label="Timezone" value={form.timezone} onChange={(v) => setForm({ ...form, timezone: v })} testid="set-timezone-input" placeholder="Asia/Kolkata" />
                    <div>
                      <Label className="label-eyebrow mb-2 inline-block">Workspace URL slug</Label>
                      <Input
                        value={form.slug}
                        onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "-") })}
                        placeholder="my-agency"
                        pattern="^[a-z0-9][a-z0-9_-]{1,38}[a-z0-9]$"
                        className="rounded-none h-10 font-mono"
                        data-testid="set-slug-input"
                      />
                      <p className="text-[11px] text-muted-foreground mt-1">Lowercase letters, digits, hyphens or underscores. 3–40 chars. <b>Owners only.</b> Changing this breaks existing share/email URLs.</p>
                    </div>
                  </div>
                </div>
                {/* Business continuity — email fallback owner */}
                <EmailContinuitySection />
                {/* R47 · Email signatures (per user + workspace default) */}
                <div className="border border-border p-6 space-y-3">
                  <div>
                    <div className="label-eyebrow">Email signatures</div>
                    <p className="text-[11px] text-muted-foreground">
                      Appended to manual emails sent from Leads, Prospects, Clients, and Contacts.
                    </p>
                  </div>
                  <EmailSignatureSettings />
                </div>
                {/* R157 · Feb 2026 — Owner visibility in assignee lists */}
                <HideOwnerToggleSection wsId={activeWorkspaceId} initial={data?.workspace?.hide_owner_from_assignees} onSaved={load} />
              </>
            )}

            {tab === "review-ai" && (
              <>
                {/* Review defaults */}
                <div className="border border-border p-6 space-y-5">
                  <div className="label-eyebrow">Review defaults</div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="label-eyebrow mb-2 inline-block">Default comment visibility</Label>
                      <Select value={form.default_comment_visibility} onValueChange={(v) => setForm({ ...form, default_comment_visibility: v })}>
                        <SelectTrigger className="h-10" data-testid="set-comment-vis-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="client">Visible to client</SelectItem>
                          <SelectItem value="internal">Internal only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="label-eyebrow mb-2 inline-block">Default review link mode</Label>
                      <Select value={form.default_review_link_mode} onValueChange={(v) => setForm({ ...form, default_review_link_mode: v })}>
                        <SelectTrigger className="h-10" data-testid="set-link-mode-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="view">View only</SelectItem>
                          <SelectItem value="comment">Comment</SelectItem>
                          <SelectItem value="approve">Comment + Approve</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Field label="Default link expiry (days)" type="number" value={form.default_link_expiry_days} onChange={(v) => setForm({ ...form, default_link_expiry_days: Number(v) })} testid="set-expiry-input" />
                    <div>
                      <Label className="label-eyebrow mb-2 inline-block">Approval mode</Label>
                      <Select value={form.approval_mode} onValueChange={(v) => setForm({ ...form, approval_mode: v })}>
                        <SelectTrigger className="h-10" data-testid="set-approval-mode-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single">Single approver</SelectItem>
                          <SelectItem value="multi">Multiple approvers (all must agree)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <ToggleRow
                    label="Auto-lock version on approve"
                    description="When a reviewer approves, the version becomes immutable."
                    value={form.auto_lock_on_approve}
                    onChange={(v) => setForm({ ...form, auto_lock_on_approve: v })}
                    testid="set-auto-lock-switch"
                  />
                </div>

                {/* AI features */}
                <div className="border border-border p-6 space-y-5" data-testid="settings-ai-section">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <div className="label-eyebrow">AI insights</div>
                  </div>
                  <p className="text-sm text-muted-foreground -mt-3">
                    Control who in this workspace can run AI summaries, comment categorization, and approval readiness scoring on review items.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="label-eyebrow mb-2 inline-block">Who can use AI on review items?</Label>
                      <Select value={form.ai_access_level} onValueChange={(v) => setForm({ ...form, ai_access_level: v })}>
                        <SelectTrigger className="h-10" data-testid="set-ai-access-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="disabled">Disabled — no AI features</SelectItem>
                          <SelectItem value="owner_admin">Owners & admins only (recommended)</SelectItem>
                          <SelectItem value="all_members">All workspace members</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Share message template */}
                <div className="border border-border p-6 space-y-5" data-testid="settings-share-section">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-primary" />
                    <div className="label-eyebrow">Share message template</div>
                  </div>
                  <p className="text-sm text-muted-foreground -mt-3">
                    Pre-filled message used when a teammate copies a review link to WhatsApp / email. Placeholders:
                    <code className="mx-1 px-1 bg-muted">{"{title}"}</code>
                    <code className="mx-1 px-1 bg-muted">{"{version}"}</code>
                    <code className="mx-1 px-1 bg-muted">{"{url}"}</code>
                    <code className="mx-1 px-1 bg-muted">{"{workspace}"}</code>
                    <code className="mx-1 px-1 bg-muted">{"{expires}"}</code>
                  </p>
                  <Textarea
                    rows={8}
                    value={form.share_message_template || ""}
                    onChange={(e) => setForm({ ...form, share_message_template: e.target.value })}
                    placeholder={`Hi! "{title}" v{version} is ready for your review.\n\nOpen: {url}\n\nQuick how-to:\n• Click anywhere to drop a pin and leave feedback\n• Drag to draw an area for region-specific notes\n• When done, click Approve or Request changes\n\nFrom: {workspace}\n{expires}\n\nThanks!`}
                    className="font-mono text-xs"
                    data-testid="set-share-template-textarea"
                  />
                </div>
              </>
            )}

            {tab === "branding" && (
              <>
                {/* Iter 126b — Global workspace brand (used on invoice PDFs,
                   public questionnaire header, client portal). Available on
                   every plan. */}
                <div className="border border-border p-6 space-y-5" data-testid="settings-brand-section">
                  <div className="flex items-center gap-2">
                    <Palette className="w-4 h-4 text-primary" />
                    <div className="label-eyebrow">Workspace brand</div>
                  </div>
                  <p className="text-sm text-muted-foreground -mt-3">
                    Applied to invoice PDFs, the public questionnaire header,
                    and the client portal. Logo URL must be publicly reachable
                    (upload via any host or use your existing CDN).
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Logo URL" value={form.logo_url || ""} onChange={(v) => setForm({ ...form, logo_url: v })} testid="ws-logo-input" placeholder="https://your-domain.com/logo.png" />
                    <Field label="Brand color (hex, e.g. #0ea5e9)" value={form.brand_color || ""} onChange={(v) => setForm({ ...form, brand_color: v })} testid="ws-brand-color-input" placeholder="#0ea5e9" />
                    <Field label="Business address (invoice PDF)" value={form.address || ""} onChange={(v) => setForm({ ...form, address: v })} testid="ws-address-input" placeholder="12 Bandra Kurla Complex, Mumbai" />
                    <Field label="GSTIN / Tax ID (invoice PDF)" value={form.gstin || ""} onChange={(v) => setForm({ ...form, gstin: v })} testid="ws-gstin-input" placeholder="29ABCDE1234F1Z5" />
                  </div>
                  <div className="flex items-center gap-4">
                    <Button onClick={save} disabled={busy} className="rounded-none" data-testid="ws-brand-save-btn">
                      {busy ? "Saving…" : "Save brand"}
                    </Button>
                    {form.logo_url && (
                      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                        Preview:
                        <img src={form.logo_url} alt="Preview" className="h-8 border border-border" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      </div>
                    )}
                    {form.brand_color && (
                      <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                        <span className="w-4 h-4 border border-border" style={{ background: form.brand_color }} /> {form.brand_color}
                      </div>
                    )}
                  </div>
                </div>

                {/* White-label branding */}
                <div className={`border p-6 space-y-5 ${whiteLabelAllowed ? "border-border" : "border-dashed border-border opacity-80"}`} data-testid="settings-whitelabel-section">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Palette className="w-4 h-4 text-primary" />
                      <div className="label-eyebrow">White-label review pages</div>
                    </div>
                    {!whiteLabelAllowed && <span className="chip chip-pending">Agency plan or higher</span>}
                  </div>
                  <p className="text-sm text-muted-foreground -mt-3">
                    Override the logo, primary color, footer text, and &quot;from&quot; email shown on public review links and approval pages.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Logo URL (https://...)" value={brand.logo_url || ""} onChange={(v) => setBrand({ ...brand, logo_url: v })} testid="brand-logo-input" placeholder="https://your-domain.com/logo.png" />
                    <Field label="Primary color HSL (e.g. 240 70% 50%)" value={brand.primary_hsl || ""} onChange={(v) => setBrand({ ...brand, primary_hsl: v })} testid="brand-color-input" placeholder="240 70% 50%" />
                    <Field label="Footer accent text" value={brand.accent_text || ""} onChange={(v) => setBrand({ ...brand, accent_text: v })} testid="brand-accent-input" placeholder="Powered by your studio" />
                    <Field label="Custom 'From' email" value={brand.custom_email_from || ""} onChange={(v) => setBrand({ ...brand, custom_email_from: v })} testid="brand-from-input" placeholder="reviews@your-domain.com" />
                  </div>
                  <Button onClick={saveBrand} disabled={!whiteLabelAllowed || brandBusy} className="rounded-none" data-testid="brand-save-btn">
                    {brandBusy ? "Saving…" : "Save branding"}
                  </Button>
                </div>
                {/* Iter 250 · Object-storage logo + icon uploader (no CDN needed). */}
                <LogoUploader wsId={activeWorkspaceId} />
                {/* R159ee · Feb 2026 — Brand Kits link. Named palette /
                    font / logo bundles that team members can apply to
                    pitch decks, portals and PDFs without manual re-entry. */}
                <div className="border border-primary/40 bg-primary/5 p-6 space-y-3" data-testid="settings-brand-kits-link">
                  <div className="flex items-center gap-2">
                    <Palette className="w-4 h-4 text-primary" />
                    <div className="label-eyebrow">Brand kits</div>
                  </div>
                  <p className="text-sm text-muted-foreground -mt-2">
                    Save named palette · font · logo bundles the whole
                    team can apply to any deliverable in one click.
                    Perfect for multi-brand agencies and campaign-
                    specific looks.
                  </p>
                  <a
                    href="/app/settings/brand-kits"
                    className="inline-flex items-center gap-1 text-primary hover:underline text-sm font-semibold"
                    data-testid="settings-brand-kits-open"
                  >
                    Manage brand kits →
                  </a>
                </div>
                {/* Feb 2026 · Mail profiles — per-brand outbound relays.
                    Each form or studio module can pin an SMTP / Resend /
                    ZeptoMail sender so campaigns match the brand. */}
                <div className="border border-primary/40 bg-primary/5 p-6 space-y-3" data-testid="settings-mail-profiles-link">
                  <div className="flex items-center gap-2">
                    <Palette className="w-4 h-4 text-primary" />
                    <div className="label-eyebrow">Mail profiles</div>
                  </div>
                  <p className="text-sm text-muted-foreground -mt-2">
                    Named outbound-email identities per brand — SMTP, Resend or ZeptoMail.
                    Point each form or studio module at a profile so every send matches the sender domain.
                  </p>
                  <a
                    href="/app/settings/mail-profiles"
                    className="inline-flex items-center gap-1 text-primary hover:underline text-sm font-semibold"
                    data-testid="settings-mail-profiles-open"
                  >
                    Manage mail profiles →
                  </a>
                </div>
                {/* R159zz · Feb 2026 — Custom project status catalogue.
                    Rename + colour the 12 canonical statuses; flows
                    everywhere from the Planning Board picker to the
                    Projects list column and Present-mode pills. */}
                <div className="border border-primary/40 bg-primary/5 p-6 space-y-3" data-testid="settings-project-statuses-link">
                  <div className="flex items-center gap-2">
                    <Palette className="w-4 h-4 text-primary" />
                    <div className="label-eyebrow">Project statuses</div>
                  </div>
                  <p className="text-sm text-muted-foreground -mt-2">
                    Rename each project status and pick its chip colour
                    to match how your team actually talks about work.
                    Underlying keys stay stable so dashboards and
                    integrations keep working.
                  </p>
                  <a
                    href="/app/settings/project-statuses"
                    className="inline-flex items-center gap-1 text-primary hover:underline text-sm font-semibold"
                    data-testid="settings-project-statuses-open"
                  >
                    Manage project statuses →
                  </a>
                </div>
                {/* Feb 2026 R293k · Workspace-level People Labels catalogue.
                    Admin defines flat list ("Account Manager",
                    "Creative Lead", etc.); each label surfaces as a
                    column on the Projects list and as a picker on each
                    project's Overview tab. */}
                <div className="border border-primary/40 bg-primary/5 p-6 space-y-3" data-testid="settings-people-labels-link">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary" />
                    <div className="label-eyebrow">Project people labels</div>
                  </div>
                  <p className="text-sm text-muted-foreground -mt-2">
                    Define the role chips your team pins to every project
                    (Account Manager, Creative Lead, QA Reviewer…).
                    Each label becomes a column on the Projects list
                    and a picker on the project Overview.
                  </p>
                  <a
                    href="/app/settings/people-labels"
                    className="inline-flex items-center gap-1 text-primary hover:underline text-sm font-semibold"
                    data-testid="settings-people-labels-open"
                  >
                    Manage people labels →
                  </a>
                </div>
                {/* R268q · Feb 2026 — Organization billing profile.
                    Auto-fills every quote/invoice/credit-note with your
                    legal name, GSTIN, PAN, bank details, logo and lets
                    you pick a colour/layout preset for branded PDFs. */}
                <div className="border border-primary/40 bg-primary/5 p-6 space-y-3" data-testid="settings-modules-link">
                  <div className="flex items-center gap-2">
                    <ToggleLeft className="w-4 h-4 text-primary" />
                    <div className="label-eyebrow">Workspace modules</div>
                  </div>
                  <p className="text-sm text-muted-foreground -mt-2">
                    Turn optional modules on or off for the whole
                    workspace. If Brand is off, the Brand column,
                    dropdown and filter disappear across Projects,
                    Clients, Content Calendar and Studio Booking.
                  </p>
                  <a
                    href="/app/settings/modules"
                    className="inline-flex items-center gap-1 text-primary hover:underline text-sm font-semibold"
                    data-testid="settings-modules-open"
                  >
                    Manage modules →
                  </a>
                </div>
                {/* R268q · Feb 2026 — Organization billing profile.
                    Auto-fills every quote/invoice/credit-note with your
                    legal name, GSTIN, PAN, bank details, logo and lets
                    you pick a colour/layout preset for branded PDFs. */}
                <div className="border border-primary/40 bg-primary/5 p-6 space-y-3" data-testid="settings-billing-profile-link">
                  <div className="flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-primary" />
                    <div className="label-eyebrow">Billing profile & PDF templates</div>
                  </div>
                  <p className="text-sm text-muted-foreground -mt-2">
                    Configure your legal entity, GSTIN, bank account and
                    logo once — every quote, invoice and credit note
                    picks it up automatically. Choose a layout preset
                    and brand colours for polished GST-compliant PDFs.
                  </p>
                  <a
                    href="/app/settings/billing-profile"
                    className="inline-flex items-center gap-1 text-primary hover:underline text-sm font-semibold"
                    data-testid="settings-billing-profile-open"
                  >
                    Configure billing profile →
                  </a>
                </div>
                {/* Iter 250 · Super-admin platform settings (hidden for non-SA). */}
                <PlatformSettingsSection />
                {/* Appearance */}
                <AppearanceSection />
                {/* Feb 2026 R293zw · Workspace session timeout policy */}
                <SessionTimeoutSection />
              </>
            )}

            {tab === "themes" && (
              <FestivalThemesSection />
            )}

            {tab === "engagement" && (
              <>
                {/* Points engine */}
                <PointsRulesSection />
                {/* Client portal per-role visibility */}
                <PortalVisibilitySection />
              </>
            )}

            {tab === "integrations" && (
              <>
                <IntegrationsSection />
                {/* Feb 2026 · Developer Portal — Personal Access Tokens
                    live in Integrations tab (colocated with SMTP / webhook
                    glue for API consumers). */}
                <div className="border border-primary/40 bg-primary/5 p-6 space-y-3 mt-4" data-testid="settings-developer-link">
                  <div className="flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-primary" />
                    <div className="label-eyebrow">Developer · API tokens</div>
                  </div>
                  <p className="text-sm text-muted-foreground -mt-2">
                    Generate scoped, revocable API keys to power your own
                    mobile app, automations, or third-party integrations.
                    Per-token rate limits and optional IP allow-lists keep
                    them safe.
                  </p>
                  <a
                    href="/app/settings/developer"
                    className="inline-flex items-center gap-1 text-primary hover:underline text-sm font-semibold"
                    data-testid="settings-developer-open"
                  >
                    Manage API tokens →
                  </a>
                </div>
              </>
            )}

            {tab === "timesheet" && (
              <TimesheetSettingsSection />
            )}

            {tab === "communications" && (
              <CommunicationsSettingsSection />
            )}

            {tab === "pip" && (
              <PIPSettingsSection />
            )}

            {tab === "notifications" && (
              <div className="space-y-4">
                <div className="border border-border p-6 space-y-5">
                  <div className="label-eyebrow">Notifications</div>
                  <ToggleRow
                    label="Email notifications"
                    description="Send transactional emails (invites, approval requests, payment receipts)."
                    value={form.email_notifications_enabled}
                    onChange={(v) => setForm({ ...form, email_notifications_enabled: v })}
                    testid="set-email-notif-switch"
                  />
                </div>
                <WeeklyRecapCard />
                <NotificationPrefsPane />
              </div>
            )}

            {tab === "security" && (
              <ChangePasswordCard />
            )}

            {tab === "advanced" && (
              <>
                <AuditBackfillBlock />
                <ImpersonationSessionsBlock />
                <div className="border border-red-500/40 p-6 space-y-4 bg-red-500/5" data-testid="settings-danger-zone">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  <div className="label-eyebrow text-red-500">Data & privacy</div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Export all workspace data as JSON (GDPR/DPDP-compliant) before performing any destructive action.
                </p>
                <Button onClick={exportData} variant="outline" className="rounded-none" data-testid="settings-export-btn">
                  <Download className="w-4 h-4 mr-2" /> Export workspace data (JSON)
                </Button>
                <DeleteWorkspaceBlock />
                <CloseAccountBlock />
              </div>
              </>
            )}
          </>
        )}
      </SettingsTabsShell>
    </>
  );
}

function SettingsTabsShell({ children }) {
  const [sp, setSp] = useSearchParams();
  const active = sp.get("tab") && SETTINGS_TABS.find((t) => t.key === sp.get("tab")) ? sp.get("tab") : "general";
  const setActive = (k) => setSp((prev) => {
    const next = new URLSearchParams(prev);
    if (k === "general") next.delete("tab"); else next.set("tab", k);
    return next;
  });
  return (
    <>
      <div className="border-b border-border bg-muted/20 sticky top-0 z-10" data-testid="settings-tabs">
        <div className="px-4 sm:px-6 md:px-10 flex gap-0 overflow-x-auto">
          {SETTINGS_TABS.map((t) => {
            const Icon = t.icon;
            const on = t.key === active;
            return (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                className={`px-3.5 py-2.5 text-xs font-semibold inline-flex items-center gap-1.5 border-b-2 -mb-px transition whitespace-nowrap ${on ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                data-testid={`settings-tab-${t.key}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="px-4 sm:px-6 md:px-10 py-8 max-w-4xl space-y-6" data-testid={`settings-tab-content-${active}`}>
        {children(active)}
      </div>
    </>
  );
}


// ─────────────────── Small inline helpers ───────────────────

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <Label className="label-eyebrow mb-2 inline-block">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-10" data-testid={testid} />
    </div>
  );
}

function ToggleRow({ label, description, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <Label className="font-semibold text-sm">{label}</Label>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <Switch checked={!!value} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

/* ─── Weekly personal recap card (Iter 212) ─────────────────────── */
function WeeklyRecapCard() {
  const [optin, setOptin] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    api.get(`/me/weekly-recap/optin`)
      .then((r) => setOptin(!!r.data?.optin))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (v) => {
    setOptin(v);
    try { await api.put(`/me/weekly-recap/optin`, { optin: v }); toast.success(v ? "You're in" : "Opted out"); }
    catch (e) { toast.error("Failed to save preference"); setOptin(!v); }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/me/weekly-recap/send-now`);
      toast.success(`Sent to ${data.sent_to}`);
    } catch (e) { toast.error("Send failed"); }
    finally { setBusy(false); }
  };

  const doPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/me/weekly-recap/preview`);
      setPreview(data);
      setShowPreview(true);
    } catch (e) { toast.error("Preview failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border p-6 space-y-4" data-testid="weekly-recap-card">
      <div>
        <div className="label-eyebrow">Weekly personal recap</div>
        <p className="text-xs text-muted-foreground mt-1">
          Every Friday at 17:00 UTC, get a Claude-authored 3-sentence coaching note,
          your wins &amp; misses, and next-week hotspots. Sent to your account email.
        </p>
      </div>
      {loading ? <div className="text-xs text-muted-foreground">Loading…</div> : (
        <>
          <ToggleRow
            label="Receive weekly recap"
            description="Fridays 17:00 UTC · one email per user per workspace"
            value={optin}
            onChange={toggle}
            testid="weekly-recap-optin"
          />
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-none" onClick={doPreview} disabled={busy} data-testid="weekly-recap-preview-btn">Preview</Button>
            <Button className="rounded-none" onClick={sendNow} disabled={busy} data-testid="weekly-recap-send-now">Send test now</Button>
          </div>
        </>
      )}
      {showPreview && preview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-background border border-border max-w-2xl w-full max-h-[80vh] overflow-y-auto p-4" onClick={(e) => e.stopPropagation()} data-testid="weekly-recap-preview-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="label-eyebrow">Preview · Weekly recap</div>
              <Button size="sm" variant="ghost" className="rounded-none" onClick={() => setShowPreview(false)}>Close</Button>
            </div>
            <iframe title="recap" srcDoc={preview.html} className="w-full h-[540px] border border-border" data-testid="weekly-recap-preview-frame" />
          </div>
        </div>
      )}
    </div>
  );
}


// R157 · Feb 2026 — Toggle to hide the workspace owner from every
// assignee dropdown. Useful when a Super Admin silently owns a client
// workspace.
function HideOwnerToggleSection({ wsId, initial, onSaved }) {
  const [value, setValue] = useState(!!initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setValue(!!initial); }, [initial]);
  const save = async (v) => {
    setBusy(true);
    try {
      await api.patch(`/workspaces/${wsId}/hide-owner-flag`, { hide_owner_from_assignees: v });
      setValue(v);
      toast.success(v ? "Owner will be hidden from assignee dropdowns" : "Owner is visible everywhere");
      onSaved && onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-border p-6 space-y-3" data-testid="settings-hide-owner-section">
      <div>
        <div className="label-eyebrow">Owner visibility</div>
        <p className="text-[11px] text-muted-foreground">
          When enabled, the workspace owner is removed from every assignee dropdown, task-owner picker and meeting-invitee list. The owner still sees themselves and retains full control — only OTHER members lose them as an assignable option. Useful when a Super Admin silently owns a client workspace.
        </p>
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={value}
          onChange={(e) => save(e.target.checked)}
          disabled={busy}
          data-testid="settings-hide-owner-toggle"
        />
        Hide workspace owner from assignee dropdowns
      </label>
    </div>
  );
}
